import { SyntheticEvent, useCallback, useMemo } from "react"
import {
  Block,
  CheckCircle,
  LabelMedium,
  ListItem,
  ListItemLabel,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { RootState, selectToken, useDappSelector } from "#/redux-state"
import { FRIENDLY_DECIMALS_AMOUNT } from "#/shared/constants"
import { SwappableToken } from "#/shared/types"
import {
  bigIntToHumanReadableFormat,
  getCurrencySymbol,
  getMezoTokenName,
} from "#/shared/utils"
import { TokenIcon } from "#/ui/Token"

type SwapModalAssetSelectorItemProps = {
  currentToken: SwappableToken | null
  token: SwappableToken
  onClick: (e: SyntheticEvent<HTMLButtonElement>) => void
}

export default function SwapModalAssetSelectorItem({
  currentToken,
  onClick,
  token,
}: SwapModalAssetSelectorItemProps) {
  const [css, theme] = useStyletron()

  const { tokenBalance, tokenDecimals } = useDappSelector((state: RootState) =>
    selectToken(state, token),
  )

  const isCurrentToken = useMemo(
    () => token === currentToken,
    [token, currentToken],
  )

  const selectTokenHandler = useCallback(
    (e: SyntheticEvent<HTMLButtonElement>) => {
      e.preventDefault()
      onClick(e)
    },
    [onClick],
  )

  return (
    <ListItem
      onClick={selectTokenHandler}
      overrides={{
        Root: {
          style: () => ({
            marginBottom: theme.sizing.scale100,
            borderRadius: theme.borders.radius400,
            background: isCurrentToken
              ? theme.colors.backgroundSecondary
              : theme.colors.white,
            ":hover": {
              background: theme.colors.backgroundSecondary,
            },
          }),
        },
        Content: {
          style: () => ({
            borderBottom: "none",
            paddingTop: theme.sizing.scale200,
            paddingBottom: theme.sizing.scale200,
          }),
        },
      }}
      artworkSize={80}
      artwork={() => <TokenIcon token={token} size={theme.sizing.scale1200} />}
      endEnhancer={() => (
        <Block
          className={css({
            display: "flex",
            alignItems: "center",
            gap: theme.sizing.scale400,
          })}
        >
          <Block className={css({ textAlign: "right" })}>
            <LabelMedium>
              {bigIntToHumanReadableFormat(
                tokenBalance,
                Number(tokenDecimals),
                FRIENDLY_DECIMALS_AMOUNT,
              )}
            </LabelMedium>
            <ParagraphSmall
              color={theme.colors.contentSecondary}
              marginTop={theme.sizing.scale100}
            >
              Available
            </ParagraphSmall>
          </Block>
          {isCurrentToken && (
            <CheckCircle
              color="transparent"
              checkMarkColor={theme.colors.positive}
              size="48px"
            />
          )}
        </Block>
      )}
    >
      <ListItemLabel
        description={
          <ParagraphSmall
            as="span"
            display="block"
            marginTop={theme.sizing.scale100}
            color={theme.colors.contentSecondary}
          >
            {getCurrencySymbol(token)}
          </ParagraphSmall>
        }
      >
        {getMezoTokenName(token)}
      </ListItemLabel>
    </ListItem>
  )
}
